import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router-dom';
import { FiGrid, FiUsers, FiUserCheck, FiCalendar, FiActivity, FiBell, FiSettings, FiUserPlus, FiVideo, FiDollarSign, FiMail, FiFileText, FiMessageSquare, FiBriefcase, FiClipboard, FiLifeBuoy, FiTarget, FiCornerDownLeft, FiSend } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { useCountry } from '../../context/CountryContext';
import { emailsAPI, chatAPI, notificationsAPI, refundsAPI } from '../../services/api';
import { connectSocket } from '../../services/socket';
import logoImg from '../../assets/instabizweb.jpeg';

const Sidebar = ({ collapsed, mobileOpen, closeMobile, onHoverChange }) => {
  const { user, hasModuleAccess } = useAuth();
  const { getConfig, activeCountry } = useCountry();
  const countryKey = String(activeCountry || '');
  const config = getConfig();
  const role = String(user?.role || '').toLowerCase();
  const location = useLocation();
  const [mailboxUnreadCount, setMailboxUnreadCount] = useState(0);
  const [chatUnreadCount, setChatUnreadCount] = useState(0);
  const [ticketUnreadCount, setTicketUnreadCount] = useState(0);
  const [refundUnreadCount, setRefundUnreadCount] = useState(0);
  const readStorageKey = useMemo(() => {
    try {
      const raw = localStorage.getItem('crm_user');
      const u = raw ? JSON.parse(raw) : null;
      const id = u?.user_id || u?.email || 'anon';
      return `mailbox_read_${id}`;
    } catch (_) {
      return 'mailbox_read_anon';
    }
  }, []);

  const { t } = useTranslation();

  const allNavItems = useMemo(() => {
    const f = config.features || {};
    const items = [
      { to: '/', icon: <FiGrid />, label: t('sidebar.dashboard'), module: 'dashboard' },
      { to: '/chat', icon: <FiMessageSquare />, label: t('sidebar.discuss'), module: 'chat' },
      { to: '/tickets', icon: <FiLifeBuoy />, label: t('sidebar.tickets'), module: 'tickets' },
      { to: '/prospects', icon: <FiTarget />, label: t('sidebar.prospects'), module: 'prospects' },
      { to: '/b2b-agencies', icon: <FiUsers />, label: t('sidebar.b2bAgency'), module: 'b2b_agencies' },
      { to: '/students', icon: <FiUserCheck />, label: t('sidebar.students'), module: 'students' },
    ];
    if (f.companyModule) {
      items.push({ to: '/companies', icon: <FiBriefcase />, label: t('sidebar.companies'), module: 'companies' });
    }
    if (f.jobMapping) {
      items.push({ to: '/jobs', icon: <FiClipboard />, label: t('sidebar.jobOpenings'), module: 'jobs' });
    }
    if (f.mailAutoConfig) {
      items.push({ to: '/mail-auto-config', icon: <FiSend />, label: t('sidebar.mailAutoConfig'), module: 'mail_auto_config' });
    }
    items.push({ to: '/interviews', icon: <FiVideo />, label: t('sidebar.interviews'), module: 'interviews' });
    items.push({ to: '/mock-interviews', icon: <FiVideo />, label: t('sidebar.mockInterviews'), module: 'mock_interviews' });
    if (f.meetingRequired) {
      items.push({ to: '/meetings', icon: <FiCalendar />, label: t('sidebar.meetings'), module: 'meetings' });
    }
    items.push(
      { to: '/activities', icon: <FiActivity />, label: t('sidebar.activities'), module: 'activities' },
      { to: '/payments', icon: <FiDollarSign />, label: t('sidebar.payments'), module: 'payments' },
      { to: '/refund-requests', icon: <FiCornerDownLeft />, label: 'Refund Requests', module: 'refund_requests' },
      { to: '/agreements', icon: <FiFileText />, label: t('sidebar.agreements'), module: 'agreements' },
      { to: '/mailbox', icon: <FiMail />, label: t('sidebar.mailbox'), module: 'mailbox' },
      { to: '/email-templates', icon: <FiFileText />, label: t('sidebar.emailTemplates'), module: 'mailbox' },
      { to: '/notifications', icon: <FiBell />, label: t('sidebar.notifications'), module: 'notifications' },
      { to: '/users', icon: <FiUserPlus />, label: t('sidebar.userManagement'), module: 'users' },
      { to: '/settings', icon: <FiSettings />, label: t('sidebar.settings'), module: 'settings' },
    );
    return items;
  }, [config, t]);

  const navItems = useMemo(() => {
    return allNavItems.filter(item => {
      if (item.module === 'dashboard' && role === 'interviewer') return false;
      return hasModuleAccess(item.module);
    });
  }, [role, hasModuleAccess, allNavItems]);

  const handleNavClick = () => {
    if (closeMobile) closeMobile();
  };

  const handleMouseEnter = () => {
    if (onHoverChange) onHoverChange(true);
  };

  const handleMouseLeave = () => {
    if (onHoverChange) onHoverChange(false);
  };

  const showMailboxBadge = hasModuleAccess('mailbox');

  const refreshUnreadMailboxCount = useCallback(async () => {
    if (!showMailboxBadge) return;
    try {
      // Compute the local "received ids minus per-device read map" count and the
      // server-side notification-based count, then take the higher of the two so
      // the badge stays accurate across both devices and views.
      const [idsRes, notifRes] = await Promise.all([
        emailsAPI.getMailboxReceivedIds().catch(() => ({ data: { ids: [] } })),
        notificationsAPI.getMailboxUnreadCount().catch(() => ({ data: { count: 0 } }))
      ]);
      const ids = idsRes.data?.ids || [];
      let readMap = {};
      try {
        const raw = localStorage.getItem(readStorageKey);
        readMap = raw ? JSON.parse(raw) || {} : {};
      } catch (_) {}
      const localUnread = ids.filter((id) => !readMap[id]).length;
      const serverUnread = Number(notifRes.data?.count || 0);
      setMailboxUnreadCount(Math.max(localUnread, serverUnread));
    } catch (_) {}
  }, [readStorageKey, showMailboxBadge]);

  useEffect(() => {
    refreshUnreadMailboxCount();
    const socket = connectSocket();
    const onReadUpdate = () => refreshUnreadMailboxCount();
    const onStorage = (e) => {
      if (!e.key || e.key === readStorageKey) refreshUnreadMailboxCount();
    };
    const myUid = user?.user_id != null ? Number(user.user_id) : null;
    const isForCurrentUser = (payload) => {
      if (!payload) return true;
      if (payload.user_id == null) return true;
      return myUid != null && Number(payload.user_id) === myUid;
    };
    const notifRowIsMine = (payload) => {
      if (!payload || payload.user_id == null) return false;
      return myUid != null && Number(payload.user_id) === myUid;
    };
    const onMailboxBadge = (payload) => {
      // Server already targets this event at the specific user room, but
      // keep a defensive check so one user's badge refresh can never be
      // triggered by another user's events.
      if (!payload || myUid == null || payload.user_id == null || Number(payload.user_id) !== myUid) return;
      refreshUnreadMailboxCount();
    };
    const onNotifNewMailbox = (payload) => {
      if (payload?.related_type !== 'email') return;
      if (!notifRowIsMine(payload)) return;
      refreshUnreadMailboxCount();
    };
    const onMarkAll = (payload) => {
      // Only refresh when the event is for THIS user (or an admin "all" scope
      // that legitimately clears their own bell alongside everyone else's).
      if (!payload) { refreshUnreadMailboxCount(); return; }
      if (!isForCurrentUser(payload)) return;
      if (payload.scope === 'mailbox' || payload.scope === 'all' || payload.scope === 'user') {
        refreshUnreadMailboxCount();
      }
    };
    window.addEventListener('mailbox-read-updated', onReadUpdate);
    window.addEventListener('storage', onStorage);
    socket.on('mailbox:badge_refresh', onMailboxBadge);
    socket.on('notification:new', onNotifNewMailbox);
    socket.on('notification:mark_all_read', onMarkAll);
    const timer = setInterval(refreshUnreadMailboxCount, 60000);
    return () => {
      window.removeEventListener('mailbox-read-updated', onReadUpdate);
      window.removeEventListener('storage', onStorage);
      socket.off('mailbox:badge_refresh', onMailboxBadge);
      socket.off('notification:new', onNotifNewMailbox);
      socket.off('notification:mark_all_read', onMarkAll);
      clearInterval(timer);
    };
  }, [readStorageKey, refreshUnreadMailboxCount, countryKey, user?.user_id]);

  // ─── Chat unread badge ───
  const refreshChatUnread = useCallback(async () => {
    if (!hasModuleAccess('chat')) return;
    try {
      const res = await chatAPI.getUnreadTotal();
      setChatUnreadCount(res.data?.count || 0);
    } catch (_) {}
  }, [hasModuleAccess]);

  useEffect(() => {
    refreshChatUnread();
    const socket = connectSocket();

    const onUnreadTotal = ({ count }) => setChatUnreadCount(count || 0);
    const onChatNotification = () => setChatUnreadCount(prev => prev + 1);

    socket.on('chat:unread_total', onUnreadTotal);
    socket.on('chat:notification', onChatNotification);

    const chatTimer = setInterval(refreshChatUnread, 60000);

    const onChatRead = () => refreshChatUnread();
    window.addEventListener('chat-mark-read', onChatRead);

    return () => {
      socket.off('chat:unread_total', onUnreadTotal);
      socket.off('chat:notification', onChatNotification);
      clearInterval(chatTimer);
      window.removeEventListener('chat-mark-read', onChatRead);
    };
  }, [refreshChatUnread, countryKey]);

  // ─── Refund unread badge ───
  const refreshRefundUnread = useCallback(async () => {
    if (!hasModuleAccess('refund_requests')) return;
    try {
      const res = await refundsAPI.getUnreadCount();
      setRefundUnreadCount(res.data?.count || 0);
    } catch (_) {}
  }, [hasModuleAccess]);

  useEffect(() => {
    refreshRefundUnread();
    const socket = connectSocket();

    const myUid = user?.user_id != null ? Number(user.user_id) : null;
    const isForCurrentUser = (payload) => {
      if (!payload) return true;
      if (payload.user_id == null) return true;
      return myUid != null && Number(payload.user_id) === myUid;
    };
    const notifRowIsMine = (payload) => {
      if (!payload || payload.user_id == null) return false;
      return myUid != null && Number(payload.user_id) === myUid;
    };
    const onRefundBadge = (payload) => {
      if (!isForCurrentUser(payload)) return;
      refreshRefundUnread();
    };
    const onNotifRefund = (payload) => {
      if (payload?.related_type !== 'refund') return;
      if (!notifRowIsMine(payload)) return;
      refreshRefundUnread();
    };
    socket.on('refund:badge_update', onRefundBadge);
    socket.on('notification:new', onNotifRefund);

    const refundTimer = setInterval(refreshRefundUnread, 60000);

    const onRefundRead = () => refreshRefundUnread();
    window.addEventListener('refund-mark-read', onRefundRead);

    return () => {
      socket.off('refund:badge_update', onRefundBadge);
      socket.off('notification:new', onNotifRefund);
      clearInterval(refundTimer);
      window.removeEventListener('refund-mark-read', onRefundRead);
    };
  }, [refreshRefundUnread, countryKey, user?.user_id]);

  // ─── Ticket unread badge ───
  const refreshTicketUnread = useCallback(async () => {
    if (!hasModuleAccess('tickets')) return;
    try {
      const res = await notificationsAPI.getTicketUnreadCount();
      setTicketUnreadCount(res.data?.count || 0);
    } catch (_) {}
  }, [hasModuleAccess]);

  useEffect(() => {
    refreshTicketUnread();
    const socket = connectSocket();

    const myUid = user?.user_id != null ? Number(user.user_id) : null;
    const isForCurrentUser = (payload) => {
      if (!payload) return true;
      if (payload.user_id == null) return true;
      return myUid != null && Number(payload.user_id) === myUid;
    };
    const notifRowIsMine = (payload) => {
      if (!payload || payload.user_id == null) return false;
      return myUid != null && Number(payload.user_id) === myUid;
    };
    const onTicketBadge = (payload) => {
      if (!isForCurrentUser(payload)) return;
      refreshTicketUnread();
    };
    const onNotifNew = (payload) => {
      if (payload?.related_type !== 'ticket') return;
      if (!notifRowIsMine(payload)) return;
      refreshTicketUnread();
    };
    socket.on('ticket:badge_update', onTicketBadge);
    socket.on('notification:new', onNotifNew);

    const ticketTimer = setInterval(refreshTicketUnread, 60000);

    const onTicketRead = () => refreshTicketUnread();
    window.addEventListener('ticket-mark-read', onTicketRead);

    return () => {
      socket.off('ticket:badge_update', onTicketBadge);
      socket.off('notification:new', onNotifNew);
      clearInterval(ticketTimer);
      window.removeEventListener('ticket-mark-read', onTicketRead);
    };
  }, [refreshTicketUnread, countryKey, user?.user_id]);

  return (
    <div
      className={`sidebar ${collapsed ? 'collapsed' : ''} ${mobileOpen ? 'mobile-open' : ''}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="sidebar-logo">
        <img
          src={logoImg}
          alt={t('login.imgAlt')}
          style={{
            width: collapsed ? 36 : 60,
            height: 'auto',
            objectFit: 'contain',
            transition: 'width 0.25s ease',
            display: 'block',
            margin: collapsed ?undefined :'0 auto'
          }}
        />
      </div>

      <nav className="sidebar-nav">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/'}
            className={({ isActive }) => `${isActive ? 'active ' : ''}sidebar-nav-link`}
            title={item.label}
            onClick={handleNavClick}
          >
            <span className="nav-icon">{item.icon}</span>
            {!collapsed && <span className="nav-label">{item.label}</span>}
            {item.to === '/chat' && chatUnreadCount > 0 && (
              <span
                className={`nav-unread-badge ${collapsed ? 'collapsed' : ''}`}
                title={t('sidebar.unreadMessages', { count: chatUnreadCount })}
              >
                {chatUnreadCount > 99 ? '99+' : chatUnreadCount}
              </span>
            )}
            {item.to === '/mailbox' && mailboxUnreadCount > 0 && (
              <span
                className={`nav-unread-badge ${collapsed ? 'collapsed' : ''}`}
                title={t('sidebar.unreadEmails', { count: mailboxUnreadCount })}
              >
                {mailboxUnreadCount > 99 ? '99+' : mailboxUnreadCount}
              </span>
            )}
            {item.to === '/refund-requests' && refundUnreadCount > 0 && (
              <span
                className={`nav-unread-badge ${collapsed ? 'collapsed' : ''}`}
                title={`${refundUnreadCount} unread refund requests`}
              >
                {refundUnreadCount > 99 ? '99+' : refundUnreadCount}
              </span>
            )}
            {item.to === '/tickets' && ticketUnreadCount > 0 && (
              <span
                className={`nav-unread-badge ${collapsed ? 'collapsed' : ''}`}
                title={t('sidebar.ticketUpdates', { count: ticketUnreadCount })}
              >
                {ticketUnreadCount > 99 ? '99+' : ticketUnreadCount}
              </span>
            )}
          </NavLink>
        ))}
      </nav>
    </div>
  );
};

export default Sidebar;
